// Importar el polyfill y utilidades de animación compartidas
import '../../archives/smoothScrollPolyfill.js';
import { applyFlipTransform as applyFlipTransformShared, flip as flipAnim, startBlink as startBlinkAnim, getAnimationConfig as getAnimCfg } from '../../archives/animations.js';

// Polyfills ligeros para compatibilidad amplia
// NodeList.forEach
try {
  if (typeof window !== 'undefined' && window.NodeList && !NodeList.prototype.forEach) {
    NodeList.prototype.forEach = function(callback, thisArg) {
      thisArg = thisArg || window;
      for (var i = 0; i < this.length; i++) {
        callback.call(thisArg, this[i], i, this);
      }
    };
  }
} catch (_) {}
// HTMLCollection.forEach (por si acaso)
try {
  if (typeof window !== 'undefined' && window.HTMLCollection && !HTMLCollection.prototype.forEach) {
    HTMLCollection.prototype.forEach = function(callback, thisArg) {
      thisArg = thisArg || window;
      for (var i = 0; i < this.length; i++) {
        callback.call(thisArg, this[i], i, this);
      }
    };
  }
} catch (_) {}

// Utilidades de normalización numérica
function toNum(v, d) { var n = parseFloat(v); return (isFinite(n) ? n : d); }
function toInt(v, d) { var n = parseInt(v, 10); return (isFinite(n) ? n : d); }
function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

function resolvePreloaderTarget(scope, cfg) {
  let target = null;
  const doc = (typeof document !== 'undefined') ? document : null;
  
  // PRIORITY 1: Try new format with unique ID (tkna_preloader_XX)
  if (doc && cfg) {
    const tarotId = cfg.tarot_id ?? cfg.tarotId ?? cfg.id ?? cfg.instance_id;
    if (tarotId !== undefined && tarotId !== null && tarotId !== '') {
      const idStr = String(tarotId);
      try {
        // Try new unique ID format first
        const uniqueId = `tkna_preloader_${idStr}`;
        const uniqueEl = doc.getElementById(uniqueId);
        if (uniqueEl) {
          return uniqueEl;
        }
      } catch (_) {}
      
      // Fallback to old format with data-tarot-id attribute
      try {
        const escaped = (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function')
          ? window.CSS.escape(idStr)
          : idStr.replace(/"/g, '\\"');
        const byData = doc.querySelector(`#tkna_preloader[data-tarot-id="${escaped}"], .tkna-preloader[data-tarot-id="${escaped}"]`);
        if (byData) {
          return byData;
        }
      } catch (_) {
        try {
          const byData = doc.querySelector(`#tkna_preloader[data-tarot-id="${idStr}"], .tkna-preloader[data-tarot-id="${idStr}"]`);
          if (byData) {
            return byData;
          }
        } catch (_) {}
      }
    }
  }
  
  // Legacy support: check if scope itself is the preloader
  try {
    if (scope && typeof scope.matches === 'function' && (scope.matches('#tkna_preloader') || scope.matches('.tkna-preloader') || scope.matches('[id^="tkna_preloader_"]'))) {
      return scope;
    }
  } catch (_) {}
  try {
    if (scope && typeof scope.closest === 'function') {
      const maybe = scope.closest('#tkna_preloader, .tkna-preloader, [id^="tkna_preloader_"]');
      if (maybe) {
        return maybe;
      }
    }
  } catch (_) {}
  try {
    if (scope && typeof scope.querySelector === 'function') {
      const maybe = scope.querySelector('#tkna_preloader, .tkna-preloader, [id^="tkna_preloader_"]');
      if (maybe) {
        return maybe;
      }
    }
  } catch (_) {}

  // Final fallback
  if (doc) {
    try {
      target = doc.querySelector('#tkna_preloader, .tkna-preloader');
      if (target) {
        return target;
      }
    } catch (_) {}
  }

  return target;
}

/**
 * Update the readings counter in UserAccessCard after a reading is completed
 * @param {Array} metaDataArray - Array of card results with readings info
 * @param {HTMLElement} scope - The scope element to search within (for multi-instance support)
 */
function updateAccessCardCounter(metaDataArray, scope) {
  try {
    // Check if we have readings info in the response
    if (!Array.isArray(metaDataArray) || metaDataArray.length === 0) return;
    
    const firstResult = metaDataArray[0];
    if (!firstResult || typeof firstResult.readings_remaining === 'undefined') return;

    // Determine search context: scope-specific or document-wide fallback
    const searchContext = (scope && typeof scope.querySelector === 'function') ? scope : document;
    
    // Try to extract tarot_id from scope or config
    let tarotId = null;
    if (scope) {
      // Try from scope's config
      const scopeConfig = scope.__tknaConfig || {};
      tarotId = scopeConfig.tarot_id || null;
      
      // Try from scope's data-tarot-id attribute
      if (!tarotId && typeof scope.getAttribute === 'function') {
        const dataId = scope.getAttribute('data-tarot-id');
        if (dataId) {
          tarotId = parseInt(dataId, 10) || null;
        }
      }
    }

    // Strategy: Find the correct UserAccessCard
    let accessCard = null;
    
    // PRIORITY 1: If we have tarotId, search globally by data-tarot-id (skip scope searches)
    if (tarotId) {
      const allAccessCards = document.querySelectorAll('.tkina-user-access-card');
      for (const card of allAccessCards) {
        const cardTarotId = card.getAttribute('data-tarot-id');
        if (cardTarotId && parseInt(cardTarotId, 10) === tarotId) {
          accessCard = card;
          break;
        }
      }
    }
    
    // FALLBACK 2: Scope-based searches (only if no tarotId)
    if (!accessCard && scope && !tarotId) {
      // Try within the scope itself
      accessCard = searchContext.querySelector('.tkina-user-access-card');
      
      // Try in parent container
      if (!accessCard && scope.parentElement) {
        accessCard = scope.parentElement.querySelector('.tkina-user-access-card');
      }
      
      // Try by product_id if available
      if (!accessCard && firstResult.product_id) {
        const allAccessCards = document.querySelectorAll('.tkina-user-access-card');
        for (const card of allAccessCards) {
          const cardProductId = card.getAttribute('data-product-id');
          if (cardProductId && parseInt(cardProductId, 10) === parseInt(firstResult.product_id, 10)) {
            accessCard = card;
            break;
          }
        }
      }
    }
    
    // Fallback: global search (legacy behavior for single instance)
    if (!accessCard) {
      accessCard = document.querySelector('.tkina-user-access-card');
    }
    
    if (!accessCard) return;

    // Find the readings text element inside the compact card
    const readingsTextElement = accessCard.querySelector('.tkina-readings-text-small');
    if (!readingsTextElement) return;

    const remaining = parseInt(firstResult.readings_remaining, 10);
    if (!Number.isFinite(remaining)) return;

    // Update the counter text (singular/plural handling)
    const newText = remaining === 1 
      ? `${remaining} reading remaining`
      : `${remaining} readings remaining`;
    
    readingsTextElement.textContent = newText;

    console.log('[Tarokina Restriction] Counter updated:', remaining, 'readings remaining');
  } catch (err) {
    console.error('[Tarokina Restriction] Error updating counter:', err);
  }
}

// Acepta un contenedor raíz opcional para scopear todas las consultas DOM a la instancia
export default function(root) {
  const scope = (root && typeof root.querySelector === 'function') ? root : document;
  // Idempotencia por instancia: evita re-inicializar varias veces sobre el mismo contenedor
  if (scope && scope.__tknaD1Inited) {
    return; // ya inicializado para este scope
  }
  try { if (scope) scope.__tknaD1Inited = true; } catch (_) {}
  // Configuración scopeada por instancia (inyectada por el entry del tema) con fallback al global legacy
  const tknaCfg = scope.__tknaConfig || (typeof window !== 'undefined' ? (window.tarokina || {}) : {});
  
  // If tarot_id is not in config, try to read it from data-tarot-id attribute
  if (!tknaCfg.tarot_id && scope && typeof scope.getAttribute === 'function') {
    try {
      const dataId = scope.getAttribute('data-tarot-id');
      if (dataId) {
        tknaCfg.tarot_id = parseInt(dataId, 10) || 0;
      }
    } catch (_) {}
  }
  
  const ajaxUrl = tknaCfg.ajax_url || (typeof window !== 'undefined' ? window.ajaxurl : '');
  const ajaxNonce = tknaCfg.nonce || '';
  // Detección de soporte de variables CSS y transform 3D (degradar si no hay soporte)
  const supportsCSSVars = !!(typeof window !== 'undefined' && window.CSS && typeof window.CSS.supports === 'function' && (window.CSS.supports('(--a: 0)') || window.CSS.supports('color', 'var(--fake)')));
  const supports3D = (() => {
    try { return !!(window.CSS && CSS.supports && (CSS.supports('transform-style', 'preserve-3d') || CSS.supports('transform', 'rotateY(180deg)'))); } catch (_) { return true; }
  })();
  // Helper local que delega en el módulo compartido
  function applyFlipTransform(imgEl, flipped /*bool*/, reversed /*bool*/) {
    try { applyFlipTransformShared(imgEl, flipped, reversed); } catch (_) {}
  }
  
  ///////////////////////////////  VARIABLES  ///////////////////////////////
  // #region Variables
  ///////////////////////////////////////////////////////////////////////////
  // console debug desactivado


  let CardPicture, Cards, selector, loader_bloq, spreadContainer, tarot_theme, tarot_options, ver_meanings, tkta_start, spread, spreadN, SelectorParameters, SizeCard, CssGridGap, numberCards, tkta_mode, tkta_result_tag, flip_percentage, tkta_loader, referenceFlash;

  CardPicture = scope.querySelectorAll('.pict_img');
  selector    = scope.querySelector('#selector') || scope.querySelector('[id^="selector-"]') || scope.querySelector('.selector');
  loader_bloq = scope.querySelector('[data-role="loader-bloq"]') || scope.querySelector('#loader_bloq');
  spreadContainer = scope.querySelector('.spread.tablero_container');
  // Contenedor del flash de referencia (si la opción está activa) y su lista de posiciones
  referenceFlash = scope.querySelector('.reference_flash');
  let referencePositions = [];
  if (referenceFlash) {
    try {
      const json = referenceFlash.getAttribute('data-positions') || '[]';
      const arr = JSON.parse(json);
      if (Array.isArray(arr)) referencePositions = arr;
    } catch (_) {}
    // Mostrar desde el inicio la referencia de la primera posición (anticipación)
    const initialText = referencePositions[0] || '';
    if (initialText) {
      referenceFlash.textContent = String(initialText);
      referenceFlash.style.display = 'block';
    } else {
      referenceFlash.style.display = 'none';
    }
  }

    // NOTA: El manejador del botón de inicio se define más abajo tras inicializar tkta_start
  tarot_theme = tknaCfg.tarot_theme || {};
  const positionCardMode = (tarot_theme && typeof tarot_theme.tkta_position_card === 'string') ? tarot_theme.tkta_position_card : 'default';
  const tableroHasBack = positionCardMode === 'img_back';
  tarot_options = tknaCfg.tarot_options || {};
  tkta_mode   = tarot_theme.tkta_mode;
  tkta_result_tag = tarot_theme.tkta_result_tag || 'h3';
  flip_percentage = tarot_theme.flip_percentage / 100;
  // Forzar el modelo de loader a 'loader2' (opción unificada)
  tkta_loader = 'loader2';
  // Duraciones del loader: defaults centralizados en animations.js con override por admin
  const __animCfg = getAnimCfg();
  const loaderDurationMs = (() => {
    const v = Number((tarot_theme && tarot_theme.tkta_loader_duration_ms) || undefined);
    const def = Number(__animCfg.loaderDurationMs || 2000);
    return Number.isFinite(v) && v >= 0 ? v : def;
  })();
  spread      = tarot_theme["tkta_spread"];
  spreadN = tknaCfg.spread_N
  if (spreadN == "0") {
    spreadN = "1";
  }
  ver_meanings = tarot_theme.tkta_meanings;
  tkta_start = tarot_options.tkta_start || 'selector';

  // Delegación global: mostrar selector y ocultar botón al pulsar #readTarot (multi-instancia, robusto en tiempos)
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    const __ns = (window.tarokina = window.tarokina || {});
    if (!__ns._d1StartBound) {
      __ns._d1StartBound = true;
      document.addEventListener('click', (ev) => {
      const target = ev.target;
      if (!target || typeof target.closest !== 'function') return;
      const btn = target.closest('.start_button_container button#readTarot');
      if (!btn) return;
      const wrap = btn.closest('.start_button_container');
      if (!wrap || wrap.dataset.tknaStartHandled === '1') return;
      const instId = wrap.getAttribute('data-inst-id') || '';
      // Buscar el contenedor del selector de forma robusta (por data-inst-id y con fallback al preloader)
      const rootPre = document.querySelector(`#tkna_preloader[data-tarot-id="${instId}"]`) || (wrap ? wrap.closest('#tkna_preloader') : null) || document;
      let selectorContainerEl = instId
        ? document.querySelector(`[id^="selector_container_"][data-inst-id="${instId}"]`)
        : null;
      if (!selectorContainerEl) {
        selectorContainerEl = rootPre.querySelector('[id^="selector_container_"]') || rootPre.querySelector('.selector_container_d1') || rootPre.querySelector('.selector_container');
      }
      // Ocultar la lista de posiciones antes de calcular el scroll para evitar desajustes
      try {
        const lists = rootPre.querySelectorAll('.bloq_list_references');
        lists.forEach((el) => {
          try {
            if (typeof collapseSmooth === 'function') {
              collapseSmooth(el, 400);
            } else {
              el.classList.add('is-hidden');
            }
          } catch (_) { try { el.classList.add('is-hidden'); } catch (_) {} }
        });
      } catch (_) {}

      if (selectorContainerEl) {
        try { selectorContainerEl.classList.remove('is-hidden'); } catch (_) {}
        // Añadir clase para animación de entrada suave (definida en SCSS como .mostrar)
        try { selectorContainerEl.classList.add('mostrar'); } catch (_) {}
        // Scroll suave para alinear el contenedor al borde inferior de la ventana, tras esperar a que se asienten transiciones
        try {
          const prefersReduced = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
          const behavior = prefersReduced ? 'auto' : 'smooth';
          const doBottomAlign = () => {
            // Fallback manual si scrollIntoView con block:end no está soportado correctamente
            const manual = () => {
              try {
                const rect = selectorContainerEl.getBoundingClientRect();
                const targetY = Math.max(0, rect.bottom + (window.pageYOffset || window.scrollY || 0) - window.innerHeight);
                window.scrollTo({ top: targetY, behavior });
              } catch (_) {
                try { selectorContainerEl.scrollIntoView(true); } catch (_) {}
              }
            };
            try {
              selectorContainerEl.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
              // Asegurar alineación con un ajuste manual posterior (no intrusivo)
              setTimeout(() => { try { manual(); } catch (_) {} }, 30);
            } catch (_) {
              manual();
            }
          };
          // dos frames + pequeño retardo si hay transiciones de colapso
          const delayMs = prefersReduced ? 0 : 420;
          requestAnimationFrame(() => {
            // forzar reflow para asegurar layout
            try { void selectorContainerEl.offsetHeight; } catch (_) {}
            setTimeout(() => { requestAnimationFrame(doBottomAlign); }, delayMs);
          });
        } catch (_) {}
      }

      // Accesibilidad: mover el foco al selector mostrado
      try {
        const focusTarget = (selectorContainerEl && selectorContainerEl.querySelector) ? (selectorContainerEl.querySelector('.selector') || selectorContainerEl) : selectorContainerEl;
        if (focusTarget && typeof focusTarget.focus === 'function') {
          if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.setAttribute('tabindex', '-1');
          }
          // evitar salto de scroll tras el focus
          focusTarget.focus({ preventScroll: true });
        }
      } catch (_) {}
  // (Las listas ya se colapsaron antes de calcular el scroll)
      wrap.style.display = 'none';
      wrap.dataset.tknaStartHandled = '1';
      ev.preventDefault();
      });
    }
  }

  if (loader_bloq) {
    loader_bloq.style.display = 'none';
  }
  if (spreadContainer) {
    spreadContainer.style.opacity = '1';
  }

  ///////////////////////// PARÁMETROS  ///////////////////////////////////////
  // #region Parámetros
  /////////////////////////////////////////////////////////////////////////////
  // SelectorParameters con fallback seguro
  SelectorParameters  = (tarot_theme && tarot_theme.selector_parameters && tarot_theme.selector_parameters.d1) ? tarot_theme.selector_parameters.d1 : {};

  // Parametros desktop y movil
  if (window.innerWidth >= 768) {
    // Desktop
    SizeCard   = toInt(SelectorParameters.selector_value_1, 12);
    CssGridGap = toNum(SelectorParameters.selector_value_2, 12);
    numberCards = toInt(SelectorParameters.selector_value_4, 0);
  } else {
    // Movil
    // Fallback a desktop si faltan valores _mov
    SizeCard   = toInt((SelectorParameters.selector_value_1_mov != null ? SelectorParameters.selector_value_1_mov : SelectorParameters.selector_value_1), 12);
    CssGridGap = toNum((SelectorParameters.selector_value_2_mov != null ? SelectorParameters.selector_value_2_mov : SelectorParameters.selector_value_2), 12);
    numberCards = toInt((SelectorParameters.selector_value_4_mov != null ? SelectorParameters.selector_value_4_mov : SelectorParameters.selector_value_4), 0);
  }

  // Overwrite numberCards when used by d2 (fanned): honor CSS var --fan-count
  try {
    if (selector) {
      const st = getComputedStyle(selector);
      const cssFanRaw = st.getPropertyValue('--fan-count');
      const cssFan = parseInt(cssFanRaw, 10);
      if (selector.classList && selector.classList.contains('fanned') && Number.isFinite(cssFan) && cssFan > 0) {
        numberCards = cssFan;
      }
    }
  } catch (_) { /* noop */ }

  // Clamping a rangos razonables
  SizeCard = clamp(SizeCard, 1, 24); // permitir hasta 24 columnas si se desea
  CssGridGap = clamp(CssGridGap, 0, 96);

  // Añadir estilo de grid-template-columns
  // Usar variables CSS para que SCSS pueda aportar fallbacks/prefijos
  if (selector && selector.style && typeof selector.style.setProperty === 'function') {
    selector.style.setProperty('--tkna-cols', String(SizeCard));
    selector.style.setProperty('--tkna-gap', String(CssGridGap) + 'px');
  }

  ///////////////////////// CARTAS EN EL SELECTOR  ////////////////////////////
  // #region Cartas aleatorias
  /////////////////////////////////////////////////////////////////////////////

  var CardsArray = (typeof Array.from === 'function' ? Array.from(CardPicture) : Array.prototype.slice.call(CardPicture, 0)) || [];
  // Ordenar aleatoriamente el array de cartas
  CardsArray.sort(function() {
    return 0.5 - Math.random();
  });

  if (tkta_mode == "flip" || tkta_mode == "eflip") {
    flip_percentage = clamp(toNum(tarot_theme.flip_percentage, 0) / 100, 0, 1);
  }else{
    flip_percentage = 0;
  }

  // Generar un array de flips con un tanto por ciento de activos
  let totalCards = CardsArray.length;
  let onesCount = Math.ceil(totalCards * flip_percentage);
  let zerosCount = totalCards - onesCount;
  let flipsArray = Array(onesCount).fill(180).concat(Array(zerosCount).fill(0));
  flipsArray.sort(() => 0.5 - Math.random());

  // Asignar los valores de flipsArray a las cartas
  for (var iSet = 0; iSet < CardsArray.length; iSet++) {
    var cEl = CardsArray[iSet];
    if (cEl && cEl.setAttribute) {
      cEl.setAttribute('data-flip', flipsArray[iSet] || 0);
    }
  }

  // Recolocar cartas según el orden aleatorio
  if (selector) {
    const isFanned = !!(selector.classList && selector.classList.contains('fanned'));
    // Elimina las cartas actuales del DOM (para reordenar)
    for (var iRem = 0; iRem < CardsArray.length; iRem++) {
      var rEl = CardsArray[iRem];
      try { if (selector.contains(rEl)) selector.removeChild(rEl); } catch (_) {}
    }
    // Reinsertar SIEMPRE todas las cartas en el orden aleatorio generado
    for (var iApp = 0; iApp < CardsArray.length; iApp++) {
      try { selector.appendChild(CardsArray[iApp]); } catch (_) {}
    }
    // En d1 (no fanned), limitar visibilidad ocultando las extras
    if (!isFanned) {
      const desired = Math.max(0, Number(numberCards) || 0);
      const all = selector.querySelectorAll('.pict_img');
      for (var iVis = 0; iVis < all.length; iVis++) {
        const el = all[iVis];
        if (!el) continue;
        if (iVis < desired || desired <= 0) {
          try { el.style.removeProperty('display'); } catch (_) {}
        } else {
          try { el.style.display = 'none'; } catch (_) {}
        }
      }
    }
  }
  Cards = scope.querySelectorAll('.pict_img');
  // Estado inicial para compatibilidad: si el navegador no soporta CSS vars, asegura la rotación por defecto
  try {
    for (var iInit = 0; iInit < Cards.length; iInit++) {
      var c0 = Cards[iInit];
      var img0 = c0 ? c0.querySelector && c0.querySelector('.selectorCard') : null;
      if (!img0) continue;
      var reversed0 = !!(img0.classList && img0.classList.contains('flipSelector'));
      applyFlipTransform(img0, /*flipped*/ false, /*reversed*/ reversed0);
    }
  } catch (_) {}

  ///////////////////////// CLICK EN UNA CARTA  ///////////////////////////////////////
  // #region Hacer click
  /////////////////////////////////////////////////////////////////////////////

  let clickCount = 0;
  let moldeCount = 0;
  let promiseQueue = Promise.resolve();
  // Para usar a Ajax o a la API Fetch
  let IdsFlipArray = [];

  // Coordinación: esperar a que terminen de mostrarse todas las cartas en el tablero
  let finishedTableroCount = 0;
  let loaderLaunched = false;
  let pendingBlinkCount = 0;
  let pendingFlipCount = 0;
  let dwellTargetAt = null;
  let dwellTimerId = null;

  function maybeLaunchLoader() {
    // Total por instancia: priorizar número de moldes en el DOM del scope
    let total = 0;
    try {
      const t = scope.querySelector('.tablero');
      if (t) {
        const m = t.querySelectorAll('.molde');
        total = (m && m.length) ? m.length : 0;
      }
    } catch (_) { total = 0; }
    if (!total || total <= 0) {
      total = Number(spreadN);
    }
    if (!loaderLaunched && total > 0 && finishedTableroCount >= total) {
      try {
        const prefersReduced = (typeof window !== 'undefined' && window.matchMedia)
          ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
          : false;
        const animCfg = getAnimCfg();
        const dwellDefault = Number(animCfg.loaderDwellMs || 1200);
        const dwellMs = prefersReduced ? 0 : dwellDefault; // retardo centralizado

        if (dwellTargetAt === null) {
          dwellTargetAt = Date.now() + Math.max(0, dwellMs);
        }
        const now = Date.now();
        const dwellReady = now >= dwellTargetAt;
        const flipsDone = (pendingFlipCount <= 0);

        if (dwellReady && flipsDone) {
          loaderLaunched = true;
          dwellTargetAt = null;
          if (dwellTimerId) { try { clearTimeout(dwellTimerId); } catch (_) {} dwellTimerId = null; }
          launchResultsFlow();
        } else {
          if (!dwellReady && !dwellTimerId) {
            const wait = Math.max(0, dwellTargetAt - now);
            dwellTimerId = setTimeout(() => { dwellTimerId = null; try { maybeLaunchLoader(); } catch (_) { maybeLaunchLoader(); } }, wait);
          }
          // Si falta flip, onFadeEnd reintentará maybeLaunchLoader
        }
      } catch (_) {
        loaderLaunched = true;
        launchResultsFlow();
      }
    }
  }

  async function launchResultsFlow() {
    let selector_container, selectorEl, tkna_preloader, loader, cont_result, reThrowBlock, tkta_result_color, tabsResult, tkta_img_result, tkta_title_result;

    // Buscar contenedor del selector tanto para d1 como para d2, con fallbacks genéricos
    selector_container =
      scope.querySelector('#selector_container_d1')
      || scope.querySelector('.selector_container_d1')
      || scope.querySelector('#selector_container_d2')
      || scope.querySelector('.selector_container_d2')
      || scope.querySelector('.selector_container');
    selectorEl = selector;
    tkna_preloader = scope;
    loader = scope.querySelector('[data-role="loader"]') || scope.querySelector('#loader');
    cont_result = scope.querySelector('[data-role="cont-result"]') || scope.querySelector('#cont_result');
    reThrowBlock = scope.querySelector('[data-role="bloq-re-throw"]') || scope.querySelector('#bloq-re-throw');
    tkta_result_color = tarot_theme.tkta_result_color || '';
    if (tkta_result_color)  {
      tkta_result_color = 'style="color: ' + tkta_result_color + ' !important;"';
    } else {
      tkta_result_color = '';
    }
    tabsResult = tarot_theme.tkta_result_tabs;
    tkta_img_result = tarot_theme.tkta_img_result || 0;
    tkta_title_result = tarot_theme.tkta_title_result || 0;
    // Parámetros de visualización para tarot Sí/No (0cards)
    const yesNoShowText = spread !== '0cards' || Number(tarot_theme.yes_no_params_text || 0) === 1;

  // Iniciar loader inmediatamente tras la última carta del tablero
  await delay(0);
    if (selectorEl) selectorEl.style.display = 'none';
    if (selector_container) selector_container.remove();

    // Ocultar el contenedor de texto informativo Sí/No (spread 0cards)
    try {
      const yesNoInfo = scope.querySelector('.tkina-yes-no-info');
      if (yesNoInfo) yesNoInfo.style.display = 'none';
    } catch (_) {}

    // Desplazarse suavemente al elemento tkna_preloader
    try {
      tkna_preloader.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    } catch (_) {}

    // Mostrar loader y atenuar tablero
    if (loader_bloq) {
      loader_bloq.style.display = 'block';
      if (loader) loader.classList.add(tkta_loader);
      if (spreadContainer) spreadContainer.style.opacity = '0.2';
    }

    // Preparar datos AJAX
    const postIds = IdsFlipArray.map(item => item.dataId);
    const flips = IdsFlipArray.map(item => item.flip);

    async function fetchMetaData(postIds, flips) {
      const formData = new FormData();
      formData.append('action', 'fetch_meta');
      formData.append('nonce', tknaCfg.nonce || '');
      formData.append('post_ids', JSON.stringify(postIds));
      formData.append('flips', JSON.stringify(flips));
      formData.append('mode', tarot_theme.tkta_mode);
      formData.append('spread', spread);
      try {
        const tktaName = (tarot_options && tarot_options.tkta_name) ? String(tarot_options.tkta_name) : '';
        formData.append('tkta_name', tktaName);
      } catch (_) { formData.append('tkta_name', ''); }
      try {
        const otherIdRaw = (tarot_theme && (tarot_theme.tkta_other_reading ?? 0));
        const otherId = Number(otherIdRaw);
        if (Number.isFinite(otherId) && otherId > 0) {
          formData.append('other_tarot_id', String(otherId));
        }
      } catch (_) { /* noop */ }
      try {
        const currentIdRaw = (tknaCfg && (tknaCfg.tarot_id ?? 0));
        const currentId = Number(currentIdRaw);
        if (Number.isFinite(currentId) && currentId > 0) {
          formData.append('current_tarot_id', String(currentId));
        }
      } catch (_) { /* noop */ }

      try {
        const response = await fetch(tknaCfg.ajax_url || (typeof window !== 'undefined' ? window.ajaxurl : ''), {
          method: 'POST',
          body: formData
        });
        if (!response.ok) {
          throw new Error(`Error fetching meta data: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.success) {
          return data.data;
        } else {
          console.log(`Error fetching meta data`, data.data);
          return '';
        }
      } catch (error) {
        console.error(`AJAX error`, error);
        return '';
      }
    }

    const metaDataArray = await fetchMetaData(postIds, flips);

    if (loader_bloq) {
      await delay(loaderDurationMs);
    } else {
      await delay(Math.max(500, Math.floor(loaderDurationMs / 2)));
    }

    if (metaDataArray && Array.isArray(metaDataArray)) {
      let combinedLiContent = '';
      let checked = '';
      let imgPhp;
      metaDataArray.forEach((data, index) => {
        switch (tabsResult) {
          case 'one':
            checked = (index === 0) ? 'checked' : '';
            break;
          case 'none':
            checked = '';
            break;
          case 'all':
            checked = 'checked';
            break;
        }
        try {
          const noCardURL = '/wp-content/plugins/tarokina-2025/img/no-card.png';
          const img = (data.img === false) ?  `<img class="f${data.flip}" src="${noCardURL}" alt="no-image">` : `<img class="f${data.flip}" src="${data.img}" alt="${data.post_id}">`;
          // Ocultar imagen cuando spread es 0cards
          if (tkta_img_result && spread !== '0cards') {
            imgPhp = img;
          } else {
            imgPhp = '';
          }
          
          // Yes/No Badge for 0cards spread
          let yesnoBadge = '';
          if (spread === '0cards' && data.yesno) {
            const yesText = tarot_theme['tkta_text_yes'] || 'Yes';
            const noText = tarot_theme['tkta_text_no'] || 'No';
            const isYes = data.yesno === 'yes';
            const badgeText = isYes ? yesText : noText;
            const badgeClass = isYes ? 'tkina-yesno-badge--yes' : 'tkina-yesno-badge--no';
            yesnoBadge = `<div class="tkina_yesno_content"><div class="tkina-yesno-badge ${badgeClass}">${badgeText}</div></div>`;
          }
          
          let contenido = '';
          let meaning = tarot_theme[`tkta_${index + 1}st`];
          // Título/significado de la carta
          const nameCard_link = ( ver_meanings === 1 ) ? meaning : (data.nameCard || '');
          const nameCard_content = ( ver_meanings === 1 && Number(tkta_title_result) === 1 ) ? (data.nameCard || '') : '';
          // Texto de la carta (controlado por yes_no_params en 0cards)
          const cardText = yesNoShowText ? data.texto : '';
          if ( spreadN >= 2 ) {
            contenido = `<div class="tab_result-item">
                          <input type="checkbox" id="tab_result-${data.post_id}" class="tab_result-toggle" ${checked}>
                                  <a id="tab_${index}" ${tkta_result_color} href="#">
                                  <label class="tab_result-title" for="tab_result-${data.post_id}">
                                      <${tkta_result_tag} class="tab_result-title-wrap" ${tkta_result_color}>
                                        ${nameCard_link}
                                         <svg ${tkta_result_color} fill="currentColor" height="16px" width="16px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
                                      viewBox="0 0 330 330" xml:space="preserve">
                                  <path id="XMLID_222_" d="M250.606,154.389l-150-149.996c-5.857-5.858-15.355-5.858-21.213,0.001
                                      c-5.857,5.858-5.857,15.355,0.001,21.213l139.393,139.39L79.393,304.394c-5.857,5.858-5.857,15.355,0.001,21.213
                                      C82.322,328.536,86.161,330,90,330s7.678-1.464,10.607-4.394l149.999-150.004c2.814-2.813,4.394-6.628,4.394-10.606
                                      C255,161.018,253.42,157.202,250.606,154.389z"/>
                                  </svg>
                                      </${tkta_result_tag}>
                                    </label>
                                  </a>
                                  <div class="tab_result-content">
                                        ${imgPhp}
                                        ${nameCard_content ? `<strong>${nameCard_content}</strong>` : ''}
                                        ${yesnoBadge}
                                      ${cardText || ''}
                                  </div>
                            </div>`;
          } else {
            // Para 0cards: título y texto controlados por yes_no_params_text
            // Para otros spreads: título controlado por tkta_title_result
            const titleHtml = (spread === '0cards')
              ? (yesNoShowText ? `<h3 ${tkta_result_color}>${data.nameCard}</h3>` : '')
              : (Number(tkta_title_result) === 1 ? `<h3 ${tkta_result_color}>${data.nameCard}</h3>` : '');
            // Para 0cards: título después del yesnoBadge; para otros: título antes de la imagen
            if (spread === '0cards') {
              contenido = `<div class="tab_result-item"><input type="checkbox" id="tab_result-${data.post_id}" class="tab_result-toggle" ${checked}><a id="tab_${index}" href="#"></a>
                                  <div class="tab_result-content">
                                        ${imgPhp}
                                        ${yesnoBadge}
                                      <div style="margin-top:30px">${titleHtml}</div>
                                      ${cardText || ''}
                                  </div>
                          </div>`;
            } else {
              contenido = `<div class="tab_result-item"><input type="checkbox" id="tab_result-${data.post_id}" class="tab_result-toggle" ${checked}><a id="tab_${index}" href="#"></a>
                                  <div class="tab_result-content">
                                  ${titleHtml}
                                        ${imgPhp}
                                        ${yesnoBadge}
                                      ${cardText || ''}
                                  </div>
                          </div>`;
            }
          }
          combinedLiContent += contenido;
        } catch (error) {
          console.error(`Error fetching meta data for Post ID ${data.post_id}:`, error);
        }
      });
      const ulContent = `<div id="results" class="tab_result">${combinedLiContent}</div>`;
      if (cont_result) {
        cont_result.style.display = 'block';
        cont_result.innerHTML = ulContent;
      }
      // Ocultar #tkna_preloader_wrapper solo si el botón de nueva lectura está deshabilitado
      // Si tkta_btn_newreading === 1, el wrapper permanece visible para mostrar el botón
      try {
        const btnNewReadingEnabled = Number(tarot_theme.tkta_btn_newreading) === 1;
        const wrapper = scope.querySelector('#tkna_preloader_wrapper') || document.getElementById('tkna_preloader_wrapper');
        if (!btnNewReadingEnabled) {
          // Botón deshabilitado: ocultar wrapper según el tipo de spread
          if (spread === '0cards') {
            if (wrapper) wrapper.style.display = 'none';
          } else {
            const toggles = cont_result ? cont_result.querySelectorAll('.tab_result-toggle') : [];
            if (toggles.length > 0) {
              const lastToggle = toggles[toggles.length - 1];
              lastToggle.addEventListener('change', function() {
                try {
                  if (wrapper) {
                    wrapper.style.display = this.checked ? 'none' : '';
                  }
                } catch (_) {}
              });
            }
          }
        }
      } catch (_) {}
      if (reThrowBlock) {
        reThrowBlock.classList.remove('is-hidden');
      }
      // Volver a mostrar el contenedor de texto informativo Sí/No (spread 0cards)
      try {
        const yesNoInfo = scope.querySelector('.tkina-yes-no-info');
        if (yesNoInfo) yesNoInfo.style.display = 'block';
      } catch (_) {}
      // Update readings counter if available
      updateAccessCardCounter(metaDataArray, scope);
      if (loader_bloq) {
        if (spreadContainer) spreadContainer.style.opacity = '1';
        loader_bloq.remove();
      }
      try {
        const sep = scope.querySelector('#separatorBottom') || document.getElementById('separatorBottom');
        if (sep && typeof sep.scrollIntoView === 'function') {
          sep.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      } catch (_) {}
    }
  }

  // Helpers: reunir selección, llamar AJAX y pintar resultados
  const getSelectionArrays = () => {
    const postIds = IdsFlipArray.map((it) => parseInt(it.dataId, 10)).filter((n) => !Number.isNaN(n));
    const flipsArr = IdsFlipArray.map((it) => parseInt(it.flip, 10) || 0);
    return { postIds, flipsArr };
  };

  const callFetchMeta = async ({ postIds, flipsArr, mode, spread, tktaName }) => {
    if (!ajaxUrl || !ajaxNonce) {
      console.warn('[tarokina:d1] Falta ajaxUrl o nonce');
      return { success: false, data: [] };
    }
    const formData = new FormData();
    formData.append('action', 'fetch_meta');
    formData.append('nonce', ajaxNonce);
    formData.append('post_ids', JSON.stringify(postIds));
    formData.append('flips', JSON.stringify(flipsArr));
    formData.append('tkta_name', tktaName || '');
    formData.append('mode', mode || 'basic');
    formData.append('spread', spread || '');
    // Nuevo: enviar el ID de "otra lectura" si está configurado para usar sus textos
    try {
      const otherIdRaw = (tarot_theme && (tarot_theme.tkta_other_reading ?? 0));
      const otherId = Number(otherIdRaw);
      if (Number.isFinite(otherId) && otherId > 0) {
        formData.append('other_tarot_id', String(otherId));
      }
    } catch (_) { /* noop */ }
    // Nuevo: enviar el id de la lectura actual para calcular slug original en backend
    try {
      const currentIdRaw = (tknaCfg && (tknaCfg.tarot_id ?? 0));
      const currentId = Number(currentIdRaw);
      if (Number.isFinite(currentId) && currentId > 0) {
        formData.append('current_tarot_id', String(currentId));
      }
    } catch (_) { /* noop */ }
    const res = await fetch(ajaxUrl, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    });
    let json;
    try { json = await res.json(); } catch (_) { json = { success: false, data: [] }; }
    return json;
  };

  const renderResults = (results) => {
    // Contenedor del tablero dentro del scope de esta instancia
    const tablero = scope.querySelector('.tablero') || scope;
    if (!tablero) return;
    // Eliminar resultado previo si existe
    const prev = scope.querySelector('.tkina-results');
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
    // Crear contenedor
    const wrap = document.createElement('div');
    wrap.className = 'tkina-results';
    results.forEach((item, idx) => {
      const card = document.createElement('div');
      card.className = 'tkina-result-item';
      // Imagen
      if (item.img) {
        const img = document.createElement('img');
        img.src = item.img;
        img.alt = `card-${idx+1}`;
        img.loading = 'lazy';
        card.appendChild(img);
      }
      // Texto (HTML ya filtrado por WP en el backend)
      const text = document.createElement('div');
      text.className = 'tkina-result-text';
      try { text.innerHTML = item.texto || ''; } catch (_) { text.textContent = item.texto || ''; }
      card.appendChild(text);
      wrap.appendChild(card);
    });
    tablero.appendChild(wrap);
    // Update readings counter if available
    updateAccessCardCounter(results, scope);
  };


  function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function disableClicks() {
    for (var iDis = 0; iDis < Cards.length; iDis++) {
      try { Cards[iDis].style.pointerEvents = 'none'; } catch (_) {}
    }
  }

  function enableClicks() {
    for (var iEn = 0; iEn < Cards.length; iEn++) {
      var el = Cards[iEn];
      try {
        if (el && !el.classList.contains('clicked')) {
          el.style.pointerEvents = 'auto';
        }
      } catch (_) {}
    }
  }

  // Colapsa suavemente un elemento (altura/opacidad) y lo oculta al finalizar
  function collapseSmooth(el, duration = 500) {
    if (!el) return;
    try {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        el.style.display = 'none';
        return;
      }
      const h = el.scrollHeight;
      el.style.boxSizing = 'border-box';
      el.style.height = h + 'px';
      el.style.overflow = 'hidden';
      el.style.opacity = '1';
      el.style.transition = `height ${duration}ms ease, opacity ${Math.max(150, duration - 100)}ms ease`;
      // Forzar reflow antes de colapsar
      void el.offsetHeight;
      requestAnimationFrame(() => {
        el.style.height = '0px';
        el.style.opacity = '0';
        el.style.marginTop = '0px';
        el.style.marginBottom = '0px';
      });
      const onEnd = (ev) => {
        if (ev && ev.target !== el) return;
        el.removeEventListener('transitionend', onEnd);
        el.style.display = 'none';
        // Limpiar estilos inline para no interferir con el layout posterior
        el.style.removeProperty('height');
        el.style.removeProperty('overflow');
        el.style.removeProperty('opacity');
        el.style.removeProperty('transition');
        el.style.removeProperty('margin-top');
        el.style.removeProperty('margin-bottom');
        el.style.removeProperty('box-sizing');
      };
      el.addEventListener('transitionend', onEnd);
    } catch (_) {
      el.style.display = 'none';
    }
  }


  Cards.forEach(function(card) {
    card.addEventListener('click', function() {

      if (clickCount < spreadN && !card.classList.contains('clicked')) {
  card.classList.add('clicked');
  // Mantener la mano blanca (cursor pointer) durante parpadeos y flip; se desactiva luego


  const img      = card.querySelector('.selectorCard');
        const tkina_picture = card.querySelector('.tkina_picture');
        const source   = card.querySelector('source');
        const marcador = card.querySelector('.marcador');
        let flip = card.getAttribute('data-flip');
  // Capturar índices actuales para sincronización con tablero
  const selectionIndex = clickCount; // índice de esta selección antes de incrementar
  // Reservar inmediatamente el índice de molde para evitar colisiones en paralelo
  const currentMoldeIndex = moldeCount;
  moldeCount = currentMoldeIndex + 1;

        card.style.opacity = '1';

        // Mostrar inmediatamente el marcador con el número de selección (antes de parpadeos y flip)
        try {
          if (marcador) {
            marcador.style.display = 'block';
            marcador.innerHTML = selectionIndex + 1;
          }
        } catch (_) {}


        if (img && source) {
          img.src       = img.getAttribute('data-src');
          source.srcset = img.getAttribute('data-srcmovil');
        }



        if (flip == 180) {
          img.classList.add('flipSelector');
        }

        // No voltear aún: primero lanzaremos el parpadeo y después haremos el flip
        if (img) {
          try { img.style.visibility = 'visible'; } catch (_) {}
        }

  // Ejecutar la secuencia de esta carta en paralelo (sin bloquear otras)
  (async () => {
          // Duraciones centralizadas de animación
          const animCfg = getAnimCfg();
          const reduce = animCfg.reduce;
          const tableroFadeMs = reduce ? 0 : Math.max(0, Number(animCfg.tableroFadeMs || 1500));
          const fadeEasing = animCfg.fadeEasing || 'cubic-bezier(0.22, 1, 0.36, 1)';
          const slackMs = Number.isFinite(animCfg.transitionSlackMs) ? animCfg.transitionSlackMs : 180;
          const blinkStartDelay = 0;

          // Flip (espera) y luego inicio de parpadeo (no bloqueante)
          await flipAnim(card, img, { flipMs: reduce ? 50 : animCfg.flipMs });
          try { card.style.pointerEvents = 'none'; } catch (_) {}
          const enableFlash = Number((tarot_theme && tarot_theme.tkta_flash_animation) || 0) === 1;
          let blinkMs = 0;
          if (enableFlash && !reduce) {
            const blinkInfo = startBlinkAnim(card, { blinkOneMs: animCfg.blinkOneMs, blinkCount: animCfg.blinkCount });
            blinkMs = blinkInfo.totalMs || (animCfg.blinkOneMs * animCfg.blinkCount);
            try { pendingBlinkCount += 1; } catch (_) { pendingBlinkCount = (pendingBlinkCount || 0) + 1; }
            const slackBlink = Number.isFinite(animCfg.transitionSlackMs) ? animCfg.transitionSlackMs : 180;
            let blinkEnded = false;
            const onBlinkEnd = () => {
              if (blinkEnded) return; blinkEnded = true;
              try { card.removeEventListener('animationend', onBlinkEnd); } catch (_) {}
              try { pendingBlinkCount = Math.max(0, (pendingBlinkCount || 1) - 1); } catch (_) { pendingBlinkCount = 0; }
              try { maybeLaunchLoader(); } catch (_) {}
            };
            try { card.addEventListener('animationend', onBlinkEnd); } catch (_) {}
            setTimeout(onBlinkEnd, Math.max(500, blinkMs + slackBlink));
          }

          // ==== Sincronizar: iniciar aparición en el tablero justo después (mientras parpadea) ====
          const tablero = scope.querySelector('.tablero');
          const moldes = tablero ? tablero.querySelectorAll('.molde') : [];
          if (moldes.length && currentMoldeIndex < moldes.length) {
            let molde = moldes[currentMoldeIndex];
            const tableroImgs = molde.querySelector('img');
            const sourceImgs = molde.querySelector('source');

            if (img && tableroImgs) {
              try { tableroImgs.removeAttribute('src'); } catch(_) {}
            }

            if (IdsFlipArray[currentMoldeIndex] && IdsFlipArray[currentMoldeIndex].flip == 180) {
              try { tableroImgs.classList.add('flipTable'); } catch(_) {}
            }

            // Preparar fade-in suave ANTES de asignar las fuentes para evitar flashes
            if (tableroImgs) {
              try { tableroImgs.style.animation = 'none'; } catch(_) {}
              // 1) estado inicial sin transición para no coalescar cambios
              try { tableroImgs.style.transition = 'none'; } catch(_) {}
              try { tableroImgs.style.opacity = '0'; } catch(_) {}
              try { tableroImgs.style.visibility = 'hidden'; } catch(_) {}
              // forzar reflow para fijar el estado sin transición
              try { void tableroImgs.offsetHeight; } catch(_) {}
              // 2) activar transición para el siguiente cambio (opacity->1)
              try { tableroImgs.style.transition = `opacity ${tableroFadeMs}ms ${fadeEasing}`; } catch(_) {}
              // Mantener moveCard, pero eliminar el fade-in del contenedor para que el fade de la imagen respete tableroFadeMs
              try {
                const pictureWrap = tableroImgs.closest('.picture_tableroImgs');
                if (pictureWrap) {
                  const cs = getComputedStyle(pictureWrap);
                  const animStr = String(cs.animation || '') || String(pictureWrap.style.animation || '');
                  if (animStr && animStr.indexOf('tableroFadeIn') !== -1) {
                    const parts = animStr.split(',').map(s => s.trim()).filter(Boolean);
                    const filtered = parts.filter(p => p.indexOf('tableroFadeIn') === -1);
                    if (filtered.length > 0) {
                      pictureWrap.style.animation = filtered.join(', ');
                    } else {
                      // Si solo había tableroFadeIn, dejar sin animación
                      pictureWrap.style.animation = 'none';
                    }
                    // Asegurar visibilidad si quitamos el fade del contenedor
                    pictureWrap.style.opacity = '1';
                  }
                }
              } catch (_) {}


            // Establecer las fuentes ahora (tras el flip) y tras preparar estilos de ocultación
            if (sourceImgs && source) { try { sourceImgs.srcset = source.srcset; } catch(_) {} }
            if (tableroImgs && img)  { try { tableroImgs.src = img.src; } catch(_) {} }
              // Función para ocultar el borde de esta posición (índice 1-based en DOM)
              const hideBorder = () => {
                try {
                  const bordeEl = scope.querySelector(`#borde${selectionIndex + 1}`);
                  if (bordeEl) {
                    bordeEl.style.border = 'none';
                    bordeEl.style.boxShadow = 'none';
                    bordeEl.style.background = 'none';
                  }
                } catch (_) { /* noop */ }
              };

              // Programar acciones tras finalizar los 3 parpadeos
              requestAnimationFrame(() => {
                const reduce3 = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
                // Sincronizar: iniciar tras finalizar los parpadeos centralizados
                const startAfterBlinkMs = reduce3 ? 0 : (blinkStartDelay + (blinkMs || 0));
                // Arrancar ambos: ocultar borde, aparecer en tablero y desvanecer en d2
                setTimeout(() => {
                  // 0) Si es la primera carta seleccionada, ocultar referencias y desplazar al título
                  if (selectionIndex === 0) {
                    // Ocultar lista de referencias ANTES del scroll
                    try {
                      const listReferences = scope.querySelectorAll('.bloq_list_references');
                      if (listReferences && listReferences.length) {
                        listReferences.forEach((el) => {
                          try {
                            el.style.display = 'none';
                            el.classList.add('is-hidden');
                          } catch (_) {}
                        });
                      }
                    } catch (_) {}
                    // Hacer scroll al preloader
                    try {
                      const preloaderTarget = resolvePreloaderTarget(scope, tknaCfg);
                      if (preloaderTarget && typeof preloaderTarget.scrollIntoView === 'function') {
                        const prefersReduced2 = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
                        preloaderTarget.scrollIntoView({ behavior: prefersReduced2 ? 'auto' : 'smooth', block: 'start', inline: 'nearest' });
                      }
                    } catch (_) {
                      try {
                        const preloaderTarget = resolvePreloaderTarget(scope, tknaCfg);
                        if (preloaderTarget) {
                          preloaderTarget.scrollIntoView();
                        }
                      } catch (_) {}
                    }
                  }
                  // 1) Ocultar borde justo antes de mostrar la carta en el tablero
                  hideBorder();
                  // 1.5) Si hay reverso en tablero, voltear a frente al revelar
                  try {
                    if (tableroHasBack && molde && !molde.classList.contains('is-flipped')) {
                      molde.classList.add('is-flipped');
                    }
                  } catch (_) { /* noop */ }
                  // 2) Aparecer carta en el tablero
                  try { pendingFlipCount += 1; } catch (_) { pendingFlipCount = (pendingFlipCount || 0) + 1; }
                  try { tableroImgs.style.visibility = 'visible'; } catch(_) {}
                  try { void tableroImgs.offsetHeight; } catch(_) {}
                  // Forzar siguiente frame para garantizar que la transición de opacidad se aplique incluso con blink=0
                  try {
                    requestAnimationFrame(() => {
                      try { tableroImgs.style.opacity = '1'; } catch(_) {}
                    });
                  } catch (_) { try { setTimeout(() => { try { tableroImgs.style.opacity = '1'; } catch(_) {} }, 16); } catch (_) {} }
                  // Si la configuración pide contar al inicio del fade
                  try {
                    const cfgPhase = getAnimCfg();
                    if (cfgPhase && cfgPhase.tableroCountPhase === 'start') {
                      if (!tableroImgs.__tableroDone) {
                        tableroImgs.__tableroDone = true;
                        if (!tableroImgs.__finishedCounted) {
                          tableroImgs.__finishedCounted = true;
                          finishedTableroCount += 1;
                          maybeLaunchLoader();
                        }
                      }
                    }
                  } catch (_) {}
                  // 2.5) Ocultar la carta clicada en el selector manteniendo el hueco
                  try {
                    if (reduce) {
                      card.style.opacity = '0';
                      card.style.visibility = 'hidden';
                    } else {
                      card.style.transition = `opacity ${tableroFadeMs}ms ${fadeEasing}`;
                      // Asegurar estado inicial y forzar reflow
                      void card.offsetWidth;
                      // Iniciar desvanecimiento del elemento clicado en el selector d1
                      card.style.opacity = '0';
                      let removedSelD1 = false;
                      const onCardFadeEnd = (ev2) => {
                        if (!removedSelD1 && ev2 && ev2.target === card && ev2.propertyName === 'opacity') {
                          removedSelD1 = true;
                          try { card.removeEventListener('transitionend', onCardFadeEnd); } catch (_) {}
                          try {
                            card.style.visibility = 'hidden';
                            card.style.pointerEvents = 'none';
                            card.classList.add('is-placeholder');
                          } catch (_) {}
                        }
                      };
                      try { card.addEventListener('transitionend', onCardFadeEnd); } catch (_) {}
                      setTimeout(() => {
                        if (!removedSelD1) {
                          removedSelD1 = true;
                          try { card.removeEventListener('transitionend', onCardFadeEnd); } catch (_) {}
                          try {
                            card.style.visibility = 'hidden';
                            card.style.pointerEvents = 'none';
                            card.classList.add('is-placeholder');
                          } catch (_) {}
                        }
                      }, (typeof tableroFadeMs === 'number' ? tableroFadeMs : 600) + slackMs);
                    }
                  } catch (_) {}
                  // 3) En paralelo, iniciar el desvanecimiento de la carta en el selector d2
                  try {
                    const selectorFanned = scope.querySelector('.selector.fanned');
                    if (selectorFanned) {
                      const postId = card.getAttribute('data-postid');
                      const cardInSelector = selectorFanned.querySelector(`.pict_img[data-postid="${postId}"]`);
                      // Flags/ayudante compartidos entre handlers: tablero + d2
                      let d2Finished = !(cardInSelector && cardInSelector.parentNode);
                      let countIfBothDone = function() {
                        try {
                          if (tableroImgs && tableroImgs.__tableroDone && d2Finished && !tableroImgs.__finishedCounted) {
                            tableroImgs.__finishedCounted = true;
                            finishedTableroCount += 1;
                            maybeLaunchLoader();
                          }
                        } catch (_) {}
                      };
                      if (cardInSelector && cardInSelector.parentNode) {
                        // Notificar inmediatamente que empieza el fade en d2 para reactivar el botón de barajar
                        try { selectorFanned.dispatchEvent(new CustomEvent('tkna:d2:card-fade-start', { detail: { postId } })); } catch (_) {}
                        try { cardInSelector.style.pointerEvents = 'none'; } catch (_) {}
                        try { cardInSelector.style.transition = `opacity ${tableroFadeMs}ms ${fadeEasing}`; } catch (_) {}
                        try { void cardInSelector.offsetWidth; } catch (_) {}
                        try {
                          requestAnimationFrame(() => {
                            try { cardInSelector.style.opacity = '0'; } catch (_) {}
                          });
                        } catch (_) {
                          try { setTimeout(() => { try { cardInSelector.style.opacity = '0'; } catch (_) {} }, 16); } catch (_) {}
                        }
                        let removedSel = false;
                        const onSelFadeEnd = (ev2) => {
                          if (!removedSel && ev2 && ev2.target === cardInSelector && ev2.propertyName === 'opacity') {
                            removedSel = true;
                            try { cardInSelector.removeEventListener('transitionend', onSelFadeEnd); } catch (_) {}
                            if (cardInSelector.parentNode) {
                              try {
                                cardInSelector.style.opacity = '0';
                                cardInSelector.style.visibility = 'hidden';
                                cardInSelector.style.pointerEvents = 'none';
                                cardInSelector.classList.add('is-placeholder');
                              } catch (_) {}
                              try { selectorFanned.dispatchEvent(new CustomEvent('tkna:d2:recompute')); } catch (_) {}
                              // Marcar finalización de d2 y comprobar conjunto
                              d2Finished = true;
                              countIfBothDone();
                            }
                          }
                        };
                        try { cardInSelector.addEventListener('transitionend', onSelFadeEnd); } catch (_) {}
                        setTimeout(() => {
                          if (!removedSel) {
                            removedSel = true;
                            try { cardInSelector.removeEventListener('transitionend', onSelFadeEnd); } catch (_) {}
                            if (cardInSelector.parentNode) {
                              try {
                                cardInSelector.style.opacity = '0';
                                cardInSelector.style.visibility = 'hidden';
                                cardInSelector.style.pointerEvents = 'none';
                                cardInSelector.classList.add('is-placeholder');
                              } catch (_) {}
                              try { selectorFanned.dispatchEvent(new CustomEvent('tkna:d2:recompute')); } catch (_) {}
                              d2Finished = true;
                              countIfBothDone();
                            }
                          }
                        }, (typeof tableroFadeMs === 'number' ? tableroFadeMs : 600) + slackMs);
                      }
                    }
                  } catch (_) { /* noop */ }
                }, startAfterBlinkMs);

                // Contabilizar finalización del fade-in de esta carta en el tablero (modo 'end')
                const onFadeEnd = (ev) => {
                  if (ev && ev.target === tableroImgs && ev.propertyName === 'opacity') {
                    try { tableroImgs.removeEventListener('transitionend', onFadeEnd); } catch (_) {}
                    try { pendingFlipCount = Math.max(0, (pendingFlipCount || 1) - 1); } catch (_) { pendingFlipCount = 0; }
                    try { maybeLaunchLoader(); } catch (_) {}
                    try {
                      // Si ya se marcó en 'start', evitar doble conteo
                      if (!tableroImgs.__tableroDone) {
                        tableroImgs.__tableroDone = true;
                      }
                    } catch (_) {}
                    try {
                      if (typeof countIfBothDone === 'function') { countIfBothDone(); }
                      else if (!tableroImgs.__finishedCounted) {
                        tableroImgs.__finishedCounted = true;
                        finishedTableroCount += 1;
                        maybeLaunchLoader();
                      }
                    } catch (_) {}
                  }
                };
                try { tableroImgs.addEventListener('transitionend', onFadeEnd); } catch (_) {}
                // Fallback si no hay transición o no dispara el evento
                setTimeout(() => {
                  try { tableroImgs.removeEventListener('transitionend', onFadeEnd); } catch (_) {}
                  try { pendingFlipCount = Math.max(0, (pendingFlipCount || 1) - 1); } catch (_) { pendingFlipCount = 0; }
                  try { maybeLaunchLoader(); } catch (_) {}
                  try {
                    if (!tableroImgs.__tableroDone) {
                      tableroImgs.__tableroDone = true;
                    }
                  } catch (_) {}
                  try {
                    if (typeof countIfBothDone === 'function') { countIfBothDone(); }
                    else if (!tableroImgs.__finishedCounted) {
                      tableroImgs.__finishedCounted = true;
                      finishedTableroCount += 1;
                      maybeLaunchLoader();
                    }
                  } catch (_) {}
                }, reduce3 ? 0 : (tableroFadeMs + slackMs));
              });
            }
            // Mostrar marcador con el índice correcto (1-based) si no se mostró ya
            if (marcador && marcador.style.display !== 'block') {
              marcador.style.display = 'block';
              marcador.innerHTML = selectionIndex + 1;
            }

            // Índice de molde ya reservado al inicio del clic para evitar colisiones
          }

          // Parpadeo ya en curso; no bloquear la secuencia

          // Limpieza/visibilidad al finalizar
          //card.removeAttribute('style');
         // card.classList.add('mostrar');
          if (img) { try { img.style.visibility = 'visible'; } catch (_) {} }
          // No restablecer la opacidad si ya fue ocultada tras los parpadeos
          if (card.style.visibility !== 'hidden') {
            card.style.opacity = '1';
          }
          // Limpieza ya realizada

        })();

        let dataId = card.getAttribute('data-postid');
        // Crear un objeto con dataId y flip
        let cardData={dataId:dataId,flip:flip};
        // Añadir el objeto al array
        IdsFlipArray.push(cardData);

        
  clickCount++;

        // Si alcanzamos el límite de selección, deshabilitar las cartas restantes
        if (clickCount >= Number(spreadN)) {
          try {
            Cards.forEach((el) => {
              try { if (el && !el.classList.contains('clicked')) { el.style.pointerEvents = 'none'; } } catch (_) {}
            });
          } catch (_) {
            // Fallback por compatibilidad
            for (var iRest = 0; iRest < Cards.length; iRest++) {
              var el = Cards[iRest];
              try { if (el && !el.classList.contains('clicked')) { el.style.pointerEvents = 'none'; } } catch (_) {}
            }
          }
        }

        // Tras el clic, preparar la referencia de la siguiente posición
        if (referenceFlash) {
          const total = Number(spreadN);
          const reduce = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
          if (clickCount >= total) {
            // Mantener el alto del contenedor para evitar salto de layout en la última carta
            referenceFlash.textContent = '\u00A0'; // espacio no separable
            referenceFlash.style.display = 'block';
            referenceFlash.style.opacity = '1';
          } else {
            const nextText = referencePositions[clickCount] || '';
            if (reduce) {
              referenceFlash.textContent = String(nextText || '');
              referenceFlash.style.display = nextText ? 'block' : 'none';
            } else {
              // Pequeña animación de desvanecimiento al cambiar el contenido
              referenceFlash.style.transition = 'opacity 150ms ease';
              referenceFlash.style.willChange = 'opacity';
              referenceFlash.style.opacity = '0';
              const swap = () => {
                if (nextText) {
                  referenceFlash.textContent = String(nextText);
                  referenceFlash.style.display = 'block';
                  requestAnimationFrame(() => {
                    referenceFlash.style.opacity = '1';
                    // limpiar will-change después del repintado
                    setTimeout(() => { referenceFlash.style.removeProperty('will-change'); }, 180);
                  });
                } else {
                  // Si no hay siguiente texto (caso raro salvo última), desvanecer y ocultar
                  referenceFlash.textContent = '';
                  // finalizar ocultando tras la animación
                  setTimeout(() => {
                    referenceFlash.style.display = 'none';
                    referenceFlash.style.removeProperty('will-change');
                  }, 160);
                }
              };
              setTimeout(swap, 160);
            }
          }
        }


        if (clickCount === Number(spreadN)) {
          // Ahora el loader se lanza cuando termine de imprimirse la última carta en el tablero.
          // La coordinación sucede en maybeLaunchLoader() al contar finishedTableroCount.
        }


  // NOTA: El conteo y disparo AJAX ya se gestiona en el bloque "RESPUESTA" más arriba.

      }


      
    });
  });



  // ====== Responsividad: actualizar nº de cartas visibles y estilos en resize ======
  try {
    const debounce = (fn, wait) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); }; };
    const getInstanceParams = (instRoot, selEl) => {
      try {
        const cfg =
          (selEl && selEl.__tknaConfig)
          || (instRoot && instRoot.__tknaConfig)
          || (selEl && selEl.closest && (selEl.closest('.tarokina-theme')?.__tknaConfig))
          || (selEl && selEl.closest && (selEl.closest('#tkna_preloader')?.__tknaConfig))
          || (typeof window !== 'undefined' ? (window.tarokina || {}) : {});
        const params = (cfg.tarot_theme && cfg.tarot_theme.selector_parameters && cfg.tarot_theme.selector_parameters.d1) ? cfg.tarot_theme.selector_parameters.d1 : {};
        return params || {};
      } catch (_) { return {}; }
    };
    const applyToSelector = (instRoot, selEl) => {
      if (!selEl || (selEl.classList && selEl.classList.contains('fanned'))) return; // d2 no
      try {
        const params = getInstanceParams(instRoot, selEl);
        const mobile = (typeof window !== 'undefined' && window.matchMedia)
          ? window.matchMedia('(max-width: 767.98px)').matches
          : ((typeof window !== 'undefined' ? window.innerWidth : 1024) < 768);
        const newCols = mobile
          ? toInt((params.selector_value_1_mov != null ? params.selector_value_1_mov : params.selector_value_1), 12)
          : toInt(params.selector_value_1, 12);
        const newGap = mobile
          ? toNum((params.selector_value_2_mov != null ? params.selector_value_2_mov : params.selector_value_2), 12)
          : toNum(params.selector_value_2, 12);
        // Resolver count: si el de móvil no existe o es <= 0, cae al de desktop
        const deskCount = toInt(params.selector_value_4, 0);
        const movRaw = (params.selector_value_4_mov != null ? params.selector_value_4_mov : null);
        const movCount = movRaw == null ? 0 : toInt(movRaw, 0);
        const newCount = mobile ? (movCount > 0 ? movCount : deskCount) : deskCount;
        const cols = clamp(newCols, 1, 24);
        const gap = clamp(newGap, 0, 96);
        try { selEl.style.setProperty('--tkna-cols', String(cols)); } catch (_) {}
        try { selEl.style.setProperty('--tkna-gap', String(gap) + 'px'); } catch (_) {}
        const cards = selEl.querySelectorAll('.pict_img');
        for (let i = 0; i < cards.length; i++) {
          const el = cards[i];
          if (!el) continue;
          if (newCount > 0 && i >= newCount) { try { el.style.display = 'none'; } catch (_) {} }
          else { try { el.style.removeProperty('display'); } catch (_) {} }
        }
      } catch (_) {}
    };
    const applyResponsiveCount = () => {
      // Si tenemos un scope concreto (no document), aplicar a ese; además, cubrir todas las instancias por seguridad
      try {
        if (selector && scope && scope !== document) {
          applyToSelector(scope, selector);
        }
      } catch (_) {}
      try {
        const allSelectors = document.querySelectorAll('.selector:not(.fanned)');
        allSelectors.forEach((sel) => {
          const instRoot = sel.closest('[id^="selector_container_"]') || sel.closest('.selector_container_d1') || sel.closest('#tkna_preloader') || sel.parentElement;
          applyToSelector(instRoot || document, sel);
        });
      } catch (_) {}
    };
    const onResize = debounce(applyResponsiveCount, 120);
    window.addEventListener('resize', onResize, { passive: true });
    setTimeout(applyResponsiveCount, 0);
  } catch (_) {}

};//end function selector d1